import { Close } from '@mui/icons-material'
import {
  IconButton,
  Stack,
  StackProps,
  Tooltip,
  TooltipProps,
  Typography,
  TypographyProps,
  styled,
} from '@mui/material'
import { ElementType, FC, ReactElement, ReactNode, useMemo } from 'react'

import { PartialAtLeast } from '../../../types'

interface MenuHeaderProps<
  PrimaryTypographyComponent extends ElementType = 'p',
  SecondaryTypographyComponent extends ElementType = 'span'
> {
  closeButton?: ReactElement
  closeButtonTooltipProps?: PartialAtLeast<TooltipProps, 'title'>
  primary?: ReactNode
  primaryTypographyProps?: TypographyProps<
    PrimaryTypographyComponent,
    { component?: PrimaryTypographyComponent }
  >
  secondary?: ReactNode
  secondaryTypographyProps?: TypographyProps<
    SecondaryTypographyComponent,
    { component?: SecondaryTypographyComponent }
  >
  onClose: () => void
}

const MenuHeader: FC<MenuHeaderProps> = ({
  closeButton,
  closeButtonTooltipProps,
  primary,
  primaryTypographyProps,
  secondary,
  secondaryTypographyProps,
  onClose,
}) => {
  const renderedCloseButton = useMemo(() => {
    const renderedButton = closeButton || (
      <IconButton size="small" onClick={onClose}>
        <Close fontSize="small" />
      </IconButton>
    )

    return closeButtonTooltipProps ? (
      <Tooltip {...closeButtonTooltipProps}>{renderedButton}</Tooltip>
    ) : (
      renderedButton
    )
  }, [closeButton, closeButtonTooltipProps, onClose])

  return (
    <Container alignItems={secondary ? 'flex-start' : 'center'} dense={!secondary}>
      <LabelContainer>
        <Typography fontWeight="bold" {...primaryTypographyProps}>
          {primary}
        </Typography>

        {secondary && (
          <Typography
            color="grey.600"
            lineHeight="normal"
            marginTop={0.5}
            variant="caption"
            {...secondaryTypographyProps}
          >
            {secondary}
          </Typography>
        )}
      </LabelContainer>

      {renderedCloseButton}
    </Container>
  )
}

const Container = styled(
  (props: StackProps & { dense: boolean }) => (
    <Stack {...props} direction="row" justifyContent="space-between" gap={2} mb={1} />
  ),
  { shouldForwardProp: (prop) => prop !== 'dense' }
)(({ dense, theme }) => ({
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  padding: theme.spacing(dense ? 0.5 : 1, 1, dense ? 0.5 : 1, 2),
}))

const LabelContainer = styled(Stack)`
  /* max-width: 75%; */
  overflow-wrap: break-word;
  white-space: break-spaces;
`

export default MenuHeader
